import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Download, Printer, Send, Loader2, DollarSign, Utensils, Shirt, Accessibility, HandCoins, Flame } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const downloadCsv = (filename, headers, rows) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

const SimpleTable = ({ rows, valueLabel, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wider text-gray-400 border-b border-gray-100">
          <th className="py-2 pr-4">Name</th>
          <th className="py-2 pr-4">Email</th>
          <th className="py-2 pr-4">Phone</th>
          <th className="py-2">{valueLabel}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-gray-50">
            <td className="py-2.5 pr-4 font-medium text-gray-900">{r.name}</td>
            <td className="py-2.5 pr-4 text-gray-500">{r.email}</td>
            <td className="py-2.5 pr-4 text-gray-500">{r.phone}</td>
            <td className="py-2.5 text-gray-700">{r.value}</td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={4} className="py-8 text-center text-gray-400">Nothing to show</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const ReportsPage = () => {
  const [logistics, setLogistics] = useState(null);
  const [balances, setBalances] = useState(null);
  const [finance, setFinance] = useState(null);
  const [dinner, setDinner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [logRes, balRes, finRes, dinRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/retreat/reports/logistics`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/retreat/balances`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/retreat/finance/summary`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/retreat/reports/dinner-rsvps`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
      ]);
      setLogistics(logRes.data);
      setBalances(balRes.data);
      setFinance(finRes.data);
      setDinner(dinRes.data);
    } catch {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sendReminders = async () => {
    if (!window.confirm(`Send a balance reminder email to all ${balances.count} attendees who owe money?`)) return;
    setSendingReminders(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/balances/remind`, {}, { headers: getAuthHeaders() });
      if (res.data.sent > 0) toast.success(`${res.data.sent} reminders sent`);
      if (res.data.failed > 0) toast.error(`${res.data.failed} failed to send (check SendGrid key)`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center h-[50vh]"><Loader2 className="w-6 h-6 text-gray-300 animate-spin" /></div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="reports-page">
      <div className="mb-6 flex items-start justify-between print:hidden">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600 mt-1">Balances, vendor rosters, and logistics — export or print anything</p>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="print-report-btn">
          <Printer className="w-4 h-4 mr-1.5" /> Print
        </Button>
      </div>

      <Tabs defaultValue="balances">
        <TabsList className="mb-4 flex-wrap h-auto print:hidden" data-testid="reports-tabs">
          <TabsTrigger value="balances" data-testid="tab-balances"><DollarSign className="w-3.5 h-3.5 mr-1" />Balances</TabsTrigger>
          <TabsTrigger value="dietary" data-testid="tab-dietary"><Utensils className="w-3.5 h-3.5 mr-1" />Dietary</TabsTrigger>
          <TabsTrigger value="tshirts" data-testid="tab-tshirts"><Shirt className="w-3.5 h-3.5 mr-1" />T-Shirts</TabsTrigger>
          <TabsTrigger value="accessibility" data-testid="tab-accessibility"><Accessibility className="w-3.5 h-3.5 mr-1" />Accessibility</TabsTrigger>
          <TabsTrigger value="financial" data-testid="tab-financial"><HandCoins className="w-3.5 h-3.5 mr-1" />Financial Aid</TabsTrigger>
          <TabsTrigger value="cookout" data-testid="tab-cookout"><Flame className="w-3.5 h-3.5 mr-1" />Cookout</TabsTrigger>
        </TabsList>

        <TabsContent value="balances">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4 print:hidden">
                <div>
                  <p className="text-2xl font-bold text-gray-900" data-testid="balances-total">${balances.total_outstanding.toFixed(0)} outstanding</p>
                  <p className="text-sm text-gray-500">{balances.count} attendees still owe money{finance ? ` · overall shortfall $${finance.shortfall.toLocaleString()} (after $${finance.scholarship_total.toLocaleString()} in scholarships)` : ''}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => downloadCsv('balances-due.csv', ['Name', 'Email', 'Phone', 'Status', 'Paid', 'Balance'], balances.attendees.map(a => [a.name, a.email, a.phone, a.ticket_status, a.amount_paid, a.balance]))} data-testid="export-balances-csv">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                  </Button>
                  <Button size="sm" onClick={sendReminders} disabled={sendingReminders || balances.count === 0} className="bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600" data-testid="send-reminders-btn">
                    {sendingReminders ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                    Send Reminders to All
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="balances-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-400 border-b border-gray-100">
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Email</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Paid</th>
                      <th className="py-2 pr-4">Balance</th>
                      <th className="py-2">Last Reminded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balances.attendees.map((a) => (
                      <tr key={a.id} className="border-b border-gray-50">
                        <td className="py-2.5 pr-4 font-medium text-gray-900">{a.name}</td>
                        <td className="py-2.5 pr-4 text-gray-500">{a.email}</td>
                        <td className="py-2.5 pr-4"><Badge variant="outline" className="text-xs">{a.ticket_status}</Badge></td>
                        <td className="py-2.5 pr-4 text-gray-700">${a.amount_paid.toFixed(0)}</td>
                        <td className="py-2.5 pr-4 font-semibold text-red-600">${a.balance.toFixed(0)}</td>
                        <td className="py-2.5 text-gray-400 text-xs">{a.last_reminded_at ? new Date(a.last_reminded_at).toLocaleDateString() : 'Never'}</td>
                      </tr>
                    ))}
                    {balances.attendees.length === 0 && (
                      <tr><td colSpan={6} className="py-8 text-center text-gray-400">Everyone is paid up</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dietary">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4 print:hidden">
                <p className="text-sm text-gray-600">{logistics.dietary.length} attendees with dietary restrictions (of {logistics.total_active} active)</p>
                <Button variant="outline" size="sm" onClick={() => downloadCsv('dietary-restrictions.csv', ['Name', 'Email', 'Phone', 'Dietary Restrictions'], logistics.dietary.map(r => [r.name, r.email, r.phone, r.value]))} data-testid="export-dietary-csv">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                </Button>
              </div>
              <SimpleTable rows={logistics.dietary} valueLabel="Restrictions" testId="dietary-table" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tshirts">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4 print:hidden">
                <p className="text-sm text-gray-600">{logistics.tshirt_missing > 0 ? `${logistics.tshirt_missing} attendees have no size on file` : 'All sizes on file'}</p>
                <Button variant="outline" size="sm" onClick={() => downloadCsv('tshirt-sizes.csv', ['Name', 'Size'], logistics.tshirt_list.map(r => [r.name, r.value]))} data-testid="export-tshirts-csv">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                </Button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-testid="tshirt-counts">
                {logistics.tshirt_counts.map((s) => (
                  <div key={s.size} className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">{s.count}</p>
                    <p className="text-xs uppercase tracking-wide text-gray-500">{s.size}</p>
                  </div>
                ))}
              </div>
              <SimpleTable rows={logistics.tshirt_list.map(r => ({ ...r, email: '', phone: '' }))} valueLabel="Size" testId="tshirt-table" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accessibility">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4 print:hidden">
                <p className="text-sm text-gray-600">{logistics.accessibility.length} attendees with accessibility needs</p>
                <Button variant="outline" size="sm" onClick={() => downloadCsv('accessibility.csv', ['Name', 'Email', 'Phone', 'Accommodations'], logistics.accessibility.map(r => [r.name, r.email, r.phone, r.value]))} data-testid="export-accessibility-csv">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                </Button>
              </div>
              <SimpleTable rows={logistics.accessibility} valueLabel="Accommodations" testId="accessibility-table" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="financial">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4 print:hidden">
                <p className="text-sm text-gray-600">{logistics.financial_assistance.length} attendees requested financial assistance</p>
                <Button variant="outline" size="sm" onClick={() => downloadCsv('financial-assistance.csv', ['Name', 'Email', 'Phone', 'Response', 'Scholarship'], logistics.financial_assistance.map(r => [r.name, r.email, r.phone, r.value, r.scholarship_amount ?? '']))} data-testid="export-financial-csv">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                </Button>
              </div>
              <SimpleTable rows={logistics.financial_assistance} valueLabel="Response" testId="financial-table" />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="cookout">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              {!dinner ? (
                <p className="text-sm text-gray-400">Loading…</p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4 print:hidden">
                    <div className="flex gap-6">
                      <div><p className="text-2xl font-bold text-emerald-700" data-testid="cookout-yes-count">{dinner.yes_count}</p><p className="text-xs text-gray-500 uppercase tracking-wide">Attending</p></div>
                      <div><p className="text-2xl font-bold text-gray-700">{dinner.no_count}</p><p className="text-xs text-gray-500 uppercase tracking-wide">Can&apos;t make it</p></div>
                      <div><p className="text-2xl font-bold text-amber-600">{dinner.no_response_count}</p><p className="text-xs text-gray-500 uppercase tracking-wide">No reply yet</p></div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => downloadCsv(`cookout-attending-${dinner.yes_count}.csv`, ['Name', 'Email', 'Phone'], dinner.yes.map(r => [r.name, r.email, r.phone]))}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="export-cookout-attending-csv">
                        <Download className="w-3.5 h-3.5 mr-1.5" /> Export Attending ({dinner.yes_count})
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => downloadCsv('cookout-full-list.csv', ['Name', 'Email', 'Phone', 'RSVP'], [...dinner.yes, ...dinner.no, ...dinner.no_response].map(r => [r.name, r.email, r.phone, r.value]))} data-testid="export-cookout-csv">
                        <Download className="w-3.5 h-3.5 mr-1.5" /> Full List
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">Thursday Night Cookout — Shawn Greseth&apos;s House, 20 CR-20, Maple Plain, MN 55359</p>
                  <SimpleTable rows={[...dinner.yes, ...dinner.no, ...dinner.no_response]} valueLabel="RSVP" testId="cookout-table" />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
